import { useEffect, useMemo, useRef, useState, createContext, useContext } from 'react';
import { Headphones, ListMusic, Pause, Play, SkipBack, SkipForward, Volume2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

export interface MusicTrack {
  id: string;
  title: string;
  artist: string;
  album?: string;
  cover: string;
  audioUrl: string;
  duration: number;
  genre?: string;
  npub?: string;
  pubkey?: string;
  eventKind?: number;
  dTag?: string;
  tags?: string[][];
  createdAt?: number;
  trackNumber?: number;
}

interface AudioPlayerContextValue {
  currentTrack: MusicTrack | null;
  isPlaying: boolean;
  playTrack: (track: MusicTrack) => void;
  togglePlay: () => void;
  close: () => void;
  volume: number;
  setVolume: (volume: number) => void;
  currentTime: number;
  duration: number;
  seek: (time: number) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null);

export function AudioPlayerProvider({ children }: { children: import('react').ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTrack, setCurrentTrack] = useState<MusicTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [queue, setQueue] = useState<MusicTrack[]>([]);
  const [volume, setVolumeState] = useState(() => {
    if (typeof window === 'undefined') return 0.75;
    const stored = window.localStorage.getItem('sona-volume');
    const parsed = stored === null ? Number.NaN : Number(stored);
    return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 0.75;
  });

  const setVolume = (nextVolume: number) => {
    const normalized = Math.min(1, Math.max(0, nextVolume));
    setVolumeState(normalized);
    if (typeof window !== 'undefined') window.localStorage.setItem('sona-volume', String(normalized));
    if (audioRef.current) audioRef.current.volume = normalized;
  };

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    audio.volume = volume;
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const handleEnded = () => {
      setQueue((items) => {
        const [next, ...rest] = items;
        if (next) {
          setCurrentTrack(next);
          setCurrentTime(0);
          setDuration(next.duration > 0 ? next.duration : 0);
          audio.src = next.audioUrl;
          void audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        } else setIsPlaying(false);
        return rest;
      });
    };
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    return () => { audio.removeEventListener('timeupdate', handleTimeUpdate); audio.removeEventListener('loadedmetadata', handleLoadedMetadata); audio.removeEventListener('ended', handleEnded); audio.pause(); };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const playTrack = (track: MusicTrack) => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTrack(track);
    setCurrentTime(0);
    setDuration(track.duration > 0 ? track.duration : 0);
    audio.src = track.audioUrl;
    void audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
  };
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    if (audio.paused) void audio.play().then(() => setIsPlaying(true)); else { audio.pause(); setIsPlaying(false); }
  };
  const close = () => { audioRef.current?.pause(); setCurrentTrack(null); setIsPlaying(false); setQueue([]); };
  const seek = (nextTime: number) => { const audio = audioRef.current; if (!audio) return; audio.currentTime = nextTime; setCurrentTime(nextTime); };
  const value = useMemo(() => ({ currentTrack, isPlaying, playTrack, togglePlay, close, volume, setVolume, currentTime, duration, seek }), [currentTrack, isPlaying, volume, currentTime, duration]);
  return <AudioPlayerContext.Provider value={value}>{children}<NowPlayingBar queue={queue} setQueue={setQueue} /></AudioPlayerContext.Provider>;
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (!context) throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  return context;
}

function NowPlayingBar({ queue, setQueue }: { queue: MusicTrack[]; setQueue: React.Dispatch<React.SetStateAction<MusicTrack[]>> }) {
  const { currentTrack, isPlaying, togglePlay, close, volume, setVolume, currentTime, duration, seek } = useAudioPlayer();
  if (!currentTrack) return null;
  return <div className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-[#17151f]/95 px-4 py-3 text-white shadow-2xl backdrop-blur-xl">
    <div className="mx-auto flex max-w-7xl items-center gap-4">
      <img src={currentTrack.cover} alt="" className="hidden size-12 rounded-lg object-cover sm:block" />
      <div className="min-w-0 w-44"><p className="truncate text-sm font-semibold">{currentTrack.title}</p><p className="truncate text-xs text-white/60">{currentTrack.artist}</p></div>
      <div className="flex items-center gap-1"><Button variant="ghost" size="icon" className="text-white hover:bg-white/10 hover:text-white"><SkipBack className="size-4" /></Button><Button onClick={togglePlay} size="icon" className="size-10 rounded-full bg-violet-500 text-white hover:bg-violet-400">{isPlaying ? <Pause className="size-4 fill-current" /> : <Play className="size-4 fill-current" />}</Button><Button variant="ghost" size="icon" className="text-white hover:bg-white/10 hover:text-white"><SkipForward className="size-4" /></Button></div>
      <div className="hidden flex-1 items-center gap-3 md:flex"><span className="w-9 text-[11px] text-white/45">{formatTime(currentTime)}</span><Slider aria-label="Track progress" value={[duration > 0 ? (currentTime / duration) * 100 : 0]} onValueChange={(values) => seek(((values[0] ?? 0) / 100) * duration)} max={100} step={0.1} className="flex-1" /><span className="w-9 text-right text-[11px] text-white/45">{formatTime(duration)}</span></div>
      <div className="hidden items-center gap-2 lg:flex"><Volume2 className="size-4 text-white/60" /><Slider value={[volume * 100]} onValueChange={(values) => setVolume((values[0] ?? 75) / 100)} max={100} step={1} className="w-24" /></div>
      <Button variant="ghost" size="icon" onClick={() => setQueue((items) => items.slice(0, 0))} className={cn('text-white/60 hover:bg-white/10 hover:text-white', queue.length === 0 && 'invisible')} aria-label="Clear queue"><ListMusic className="size-4" /></Button><Button variant="ghost" size="icon" onClick={close} className="text-white/60 hover:bg-white/10 hover:text-white" aria-label="Close player"><X className="size-4" /></Button>
    </div>
  </div>;
}

function formatTime(seconds: number) { if (!Number.isFinite(seconds) || seconds < 0) return '0:00'; const minutes = Math.floor(seconds / 60); return `${minutes}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`; }

export function EmptyPlayerHint() { return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Headphones className="size-4" />Select a track to start listening</div>; }
